import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, InputNumber, Space, Spin, Typography } from "antd";
import { ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { configOptionApi } from "../../services/configOption.api";
import {
  VENTANA_ASEOS_ABIERTOS_DIAS,
  VENTANA_ASEOS_MAX_DIAS,
  VENTANA_ASEOS_MIN_DIAS,
  normalizarVentanaDias,
} from "../../utils/ventanaAseosAbiertos";

const { Text, Title } = Typography;

interface Valores {
  ventanaAseosAbiertosDias: number;
}

/**
 * AseosVentanaTab — pestaña "Aseos abiertos" de /configuracion.
 *
 * Fija la VENTANA de aseos abiertos (round 2026-10-28, pedido del dueño): cuántos días hacia
 * adelante ve y puede tomar una aseadora un aseo que todavía no tiene dueña. Antes era una
 * constante de 14 días en el código. Es un ajuste global (`general_settings`, clave
 * `ventanaAseosAbiertosDias`, entero 1..90, default 14): el servidor lo lee para la lista de la
 * aseadora, para «Tomar», para las notas y para el aviso de «un aseo nuevo entró a tu ventana»,
 * y se lo manda a la pantalla de /aseos con cada carga.
 *
 * Por qué una pestaña propia y no «Precios e IVA» u «Opciones generales»: no es un precio, y
 * «Opciones generales» es el catálogo de listas desplegables (bancos, planes…), no ajustes del
 * sistema. Comparte el mismo singleton que las otras pestañas, así que esta solo envía SU clave
 * y no pisa las demás.
 *
 * Si la carga del valor vigente FALLA (red caída, 5xx), el formulario NO se precarga con el default de
 * 14 ni deja guardar: antes mostraba 14 con «Guardar» activo, y un clic pisaba con 14 el valor real
 * (p. ej. 30) sin que la persona lo supiera. Ahora muestra el error con «Reintentar» y bloquea el
 * formulario hasta que la carga resulte bien (round 2026-10-28).
 *
 * Solo admin/superadmin (la ruta lo exige también en el servidor). Ver docs/rol-aseador.md §5.8.
 */
export default function AseosVentanaTab() {
  const [form] = Form.useForm<Valores>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [guardado, setGuardado] = useState<number | null>(null);
  /** La lectura del valor vigente falló: no se conoce el valor real, así que no se puede guardar. */
  const [errorCarga, setErrorCarga] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    const res = await configOptionApi.getGeneralSettings();
    if (!res?.data) {
      // `apiFetch` devuelve null ante cualquier error. Sin el valor real NO se inventa el default.
      form.setFieldsValue({ ventanaAseosAbiertosDias: undefined });
      setGuardado(null);
      setErrorCarga(true);
      setLoading(false);
      return;
    }
    const dias = normalizarVentanaDias(res.data.ventanaAseosAbiertosDias);
    form.setFieldsValue({ ventanaAseosAbiertosDias: dias });
    setGuardado(dias);
    setErrorCarga(false);
    setLoading(false);
  }, [form]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const guardar = async () => {
    if (errorCarga) return; // sin el valor real no se pisa nada
    let valores: Valores;
    try {
      valores = await form.validateFields();
    } catch {
      return; // AntD ya muestra el error en el campo
    }
    setSaving(true);
    try {
      const res = await configOptionApi.updateGeneralSettings({
        ventanaAseosAbiertosDias: valores.ventanaAseosAbiertosDias,
      });
      if (res) setGuardado(normalizarVentanaDias(res.data.ventanaAseosAbiertosDias));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640 }} data-testid="aseos-ventana-tab">
      <Title level={5}>Ventana de aseos abiertos</Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="¿Qué hace esta ventana?"
        description={
          <>
            <div>
              Una aseadora solo <b>ve y puede tomar</b> los aseos abiertos (sin aseadora asignada)
              de los <b>próximos días</b> que indiques aquí, contando desde hoy. Los más lejanos
              siguen abiertos para ti, pero ellas no los ven ni los toman hasta que se acercan; así
              no reservan aseos de meses adelante.
            </div>
            <div style={{ marginTop: 6 }}>
              Cuando un aseo entra a la ventana, <b>se avisa</b> a las aseadoras de ese sector
              (campana de la aplicación). Lo que ya tiene aseadora se ve siempre, y tú ves todos los
              abiertos sin importar este número.
            </div>
            <div style={{ marginTop: 6 }}>
              Si lo cambias, rige de inmediato para la lista y para «Tomar»; el siguiente aviso
              diario incluye los aseos que entren por el cambio. Valor por defecto:{" "}
              {VENTANA_ASEOS_ABIERTOS_DIAS} días.
            </div>
          </>
        }
      />
      {errorCarga && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          data-testid="aseos-ventana-error"
          message="No se pudo cargar el valor actual de la ventana"
          description="Para no pisar el valor guardado con el predeterminado, el formulario queda bloqueado hasta que la carga resulte bien. Revisa tu conexión e inténtalo de nuevo."
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={() => void cargar()}>
              Reintentar
            </Button>
          }
        />
      )}
      <Form form={form} layout="vertical" disabled={errorCarga}>
        <Form.Item
          name="ventanaAseosAbiertosDias"
          label="Días hacia adelante que ve una aseadora"
          tooltip={`Número entero entre ${VENTANA_ASEOS_MIN_DIAS} y ${VENTANA_ASEOS_MAX_DIAS}. Hoy cuenta como el día 0: con 14, ve hasta el día 14 inclusive.`}
          rules={[
            { required: true, message: "Ingresa un número de días" },
            {
              type: "integer",
              min: VENTANA_ASEOS_MIN_DIAS,
              max: VENTANA_ASEOS_MAX_DIAS,
              message: `Un número entero entre ${VENTANA_ASEOS_MIN_DIAS} y ${VENTANA_ASEOS_MAX_DIAS}`,
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%", maxWidth: 320 }}
            min={VENTANA_ASEOS_MIN_DIAS}
            max={VENTANA_ASEOS_MAX_DIAS}
            step={1}
            precision={0}
            controls={false}
            addonAfter="días"
            placeholder={String(VENTANA_ASEOS_ABIERTOS_DIAS)}
          />
        </Form.Item>

        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={guardar}
            loading={saving}
            disabled={errorCarga}
          >
            Guardar
          </Button>
          <Button
            icon={<ReloadOutlined />}
            disabled={errorCarga}
            onClick={() =>
              form.setFieldsValue({ ventanaAseosAbiertosDias: VENTANA_ASEOS_ABIERTOS_DIAS })
            }
          >
            Restaurar valor por defecto
          </Button>
          {guardado != null && (
            <Text type="secondary" style={{ fontSize: 12 }} data-testid="aseos-ventana-guardado">
              Guardado: {guardado} {guardado === 1 ? "día" : "días"}
            </Text>
          )}
        </Space>
      </Form>
    </div>
  );
}
